import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { VideoPlayerProps, VdoProgressProps, ResponsiveSizes } from 'features/player';

// const Player = styled(Flex)`
//   width: ${({ width }) => (width ? `${width}px` : '100%')};
//   height: ${({ height }) => (height ? `${height}px` : '100%')};
// `;

const Player = styled('div')<{
  width: ResponsiveSizes;
  height: ResponsiveSizes;
  maxWidth: ResponsiveSizes;
  maxHeight: ResponsiveSizes;
}>`
  ${({ width, theme: { mediaQueries } }) => {
    if (typeof width === 'object') {
      let response = '';

      if (width.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            width: ${typeof width.m === 'number' ? `${width.m}px` : width.m};
          }
        `;
      }

      if (width.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            width: ${typeof width.t === 'number' ? `${width.t}px` : width.t};
          }
        `;
      }

      if (width.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            width: ${typeof width.d === 'number' ? `${width.d}px` : width.d};
          }
        `;
      }

      return response;
    } else {
      return `
        width: ${typeof width === 'number' ? `${width}px` : width};
      `;
    }
  }};

  ${({ height, theme: { mediaQueries } }) => {
    if (typeof height === 'object') {
      let response = '';

      if (height.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            height: ${typeof height.m === 'number' ? `${height.m}px` : height.m};
          }
        `;
      }

      if (height.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            height: ${typeof height.t === 'number' ? `${height.t}px` : height.t};
          }
        `;
      }

      if (height.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            height: ${typeof height.d === 'number' ? `${height.d}px` : height.d};
          }
        `;
      }

      return response;
    } else {
      return `
        height: ${typeof height === 'number' ? `${height}px` : height};
      `;
    }
  }};

  ${({ maxWidth, theme: { mediaQueries } }) => {
    if (typeof maxWidth === 'object') {
      let response = '';

      if (maxWidth.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-width: ${typeof maxWidth.m === 'number' ? `${maxWidth.m}px` : maxWidth.m};
          }
        `;
      }

      if (maxWidth.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-width: ${typeof maxWidth.t === 'number' ? `${maxWidth.t}px` : maxWidth.t};
          }
        `;
      }

      if (maxWidth.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-width: ${typeof maxWidth.d === 'number' ? `${maxWidth.d}px` : maxWidth.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-width: ${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth};
      `;
    }
  }};

  ${({ maxHeight, theme: { mediaQueries } }) => {
    if (typeof maxHeight === 'object') {
      let response = '';

      if (maxHeight.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-height: ${typeof maxHeight.m === 'number' ? `${maxHeight.m}px` : maxHeight.m};
          }
        `;
      }

      if (maxHeight.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-height: ${typeof maxHeight.t === 'number' ? `${maxHeight.t}px` : maxHeight.t};
          }
        `;
      }

      if (maxHeight.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-height: ${typeof maxHeight.d === 'number' ? `${maxHeight.d}px` : maxHeight.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-height: ${typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight};
      `;
    }
  }};
`;

const ProtectedVideoPlayer = ({
  onTimeUpdate,
  startInSecond,
  onEnd,

  externalVideoProviderIdOrUrl,
  videoProvider,

  protectedVideoOtp,
  protectedVideoPlaybackInfo,
  protectedVideoPlayerTheme,

  width = '100%',
  height = 'auto',
  maxWidth = 'inherit',
  maxHeight = 'inherit',

  ...props
}: VideoPlayerProps): ReactElement => {
  const [player, setPlayer] = useState<any>(undefined!);
  const playerRef = React.useRef<HTMLDivElement>(null);

  const loadPlayer = useCallback(() => {
    window.playerContainer = playerRef.current;

    const vdoPlayer = new window.VdoPlayer({
      otp: protectedVideoOtp,
      playbackInfo: protectedVideoPlaybackInfo,
      theme: protectedVideoPlayerTheme || '9ae8bbe8dd964ddc9bdb932cca1cb59a',
      container: playerRef.current,
      startTime: startInSecond,
    });

    setPlayer(vdoPlayer);
  }, [protectedVideoOtp, protectedVideoPlaybackInfo]);

  useEffect(() => {
    if (!window.VdoPlayer) {
      const playerScript = document.createElement('script');
      playerScript.src = 'https://player.vdocipher.com/playerAssets/1.6.10/vdo.js';

      document.body.appendChild(playerScript);

      playerScript.addEventListener('load', () => {
        return loadPlayer();
      });
    } else {
      loadPlayer();
    }
  }, [protectedVideoOtp, protectedVideoPlaybackInfo]);

  useEffect(() => {
    if (player) {
      const progressProcess = ({ status: props }: VdoProgressProps) => {
        const { status, currentTime } = props;

        if (status === 1 && onTimeUpdate) onTimeUpdate(currentTime);
      };

      player.addEventListener('progress', progressProcess);

      //   player.addEventListener('load', () => {
      //     player.availablePlaybackRates = [0.1, 0.3, 5];
      //   });

      const endedProcess = (oi: any) => {
        if (onEnd) onEnd(player.duration);
      };

      player.addEventListener('ended', endedProcess);
    }
  }, [player]);

  useEffect(() => {
    if (player && startInSecond) {
      if (player.status === 1 || player.status === 2) {
        player.seek(startInSecond);
        player.play();
      }
    }
  }, [player, startInSecond]);

  return <Player ref={playerRef} width={width} height={height} maxWidth={maxWidth} maxHeight={maxHeight} />;
};

export default ProtectedVideoPlayer;
